import { useCallback, useEffect, useRef, useState } from 'react';

const INTERVAL_MS = 4000;
const DEFAULT_IDLE_RESUME_MS = 10000;

export const useAutoCarousel = (
  slideCount: number,
  enabled = true,
  idleResumeMs = DEFAULT_IDLE_RESUME_MS,
  initialIndex = 0,
  initialIntervalMs = INTERVAL_MS,
) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isPaused, setIsPaused] = useState(!enabled);
  const [progressKey, setProgressKey] = useState(0);
  const [isFirstCycle, setIsFirstCycle] = useState(true);
  const isPausedRef = useRef(isPaused);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasStartedRef = useRef(false);

  const intervalMs = isFirstCycle ? initialIntervalMs : INTERVAL_MS;

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (!enabled || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    setActiveIndex(initialIndex);
    setIsFirstCycle(true);
    setIsPaused(false);
    setProgressKey((key) => key + 1);
  }, [enabled, initialIndex]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCycleTimer = useCallback(() => {
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
  }, []);

  const advance = useCallback(() => {
    if (isPausedRef.current) {
      return;
    }

    setIsFirstCycle(false);
    setActiveIndex((index) => (index + 1) % slideCount);
    setProgressKey((key) => key + 1);
  }, [slideCount]);

  const scheduleResume = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      setIsPaused(false);
      setProgressKey((key) => key + 1);
    }, idleResumeMs);
  }, [clearIdleTimer, idleResumeMs]);

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(index);
      clearIdleTimer();
      setIsPaused(true);
      setProgressKey((key) => key + 1);
      scheduleResume();
    },
    [clearIdleTimer, scheduleResume],
  );

  const setIndex = useCallback((index: number) => {
    setActiveIndex(index);
    setProgressKey((key) => key + 1);
  }, []);

  const pause = useCallback(() => {
    clearIdleTimer();
    setIsPaused(true);
    setProgressKey((key) => key + 1);
  }, [clearIdleTimer]);

  const resume = useCallback(() => {
    clearIdleTimer();
    setIsPaused(false);
    setProgressKey((key) => key + 1);
  }, [clearIdleTimer]);

  useEffect(() => {
    clearCycleTimer();

    if (!enabled || isPaused) {
      return clearCycleTimer;
    }

    cycleTimerRef.current = setTimeout(advance, intervalMs);

    return clearCycleTimer;
  }, [
    advance,
    clearCycleTimer,
    enabled,
    intervalMs,
    isPaused,
    activeIndex,
    progressKey,
  ]);

  useEffect(
    () => () => {
      clearIdleTimer();
      clearCycleTimer();
    },
    [clearCycleTimer, clearIdleTimer],
  );

  return {
    activeIndex,
    isPaused,
    progressKey,
    goTo,
    setIndex,
    pause,
    resume,
    intervalMs,
  };
};
